import React, { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../config/api.config';
import { naechsteTermine, anzahlAusParameter, platzText, datumText, tourText, datumSchluessel, plusMonate, Termineintrag, TerminSlot } from '../utils/naechsteTermine';
import { terminLink } from '../utils/terminLink';
import { anfrageText } from '../utils/kalenderAnfrage';
import './NaechsteTermine.css';

/** Nachrichtentyp für die Höhe des einbettenden Fensters (Website-Baustein hört nur auf https://frankenfloss.net). */
export const HOEHE_NACHRICHT = 'ff-termine-hoehe';

/**
 * T-190 (v1.8.64, Session 46, 09.10.2026): öffentliche Seite /termine mit den nächsten Terminen, zum Einbetten auf
 * frankenfloss.de (Abschnitt oder Popup, Kundin 06.10.2026 Punkt 4, Zusage Mail 07.10.2026 Punkt 4b). Ohne Kopf- und
 * Fußbereich der Anwendung. Abfrage ohne Anmeldedaten (immer Gästesicht). Knöpfe öffnen den Buchungskalender mit Datum
 * und Uhrzeit im ganzen Browserfenster (target _top), damit Anmeldung und Zahlung nicht im eingebetteten Fenster laufen.
 * Parameter: anzahl (1 bis 20, Standard 6), tour (Code einer Tourart), titel=0 (ohne Überschrift).
 * Review v1.8.64: index.tsx rendert die Seite ohne AuthProvider, PaymentProvider und VersionWatcher (im eingebetteten Fenster
 * kann der Browser den Speicher sperren, ein Zugriff würde die ganze Anwendung abbrechen); diese Seite nutzt keinen Speicher.
 */
const NaechsteTermine: React.FC = () => {
  // Parameter direkt aus der Adresse (die Seite braucht keinen Router-Zustand)
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const anzahl = anzahlAusParameter(params.get('anzahl'));
  const tour = params.get('tour');
  const mitTitel = params.get('titel') !== '0';
  const [termine, setTermine] = useState<Termineintrag[] | null>(null);
  const [namen, setNamen] = useState<Record<string, string>>({});
  const [fehler, setFehler] = useState(false);
  const wurzel = useRef<HTMLDivElement>(null);

  // Hintergrund durchsichtig (App.css setzt für body #FBF9F5), damit die Liste auf der Website ohne farbigen Rahmen erscheint;
  // die Seite hat eine eigene Wurzel (index.tsx), betrifft also nur /termine
  useEffect(() => {
    const vorher = [document.documentElement.style.background, document.body.style.background];
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';
    return () => {
      document.documentElement.style.background = vorher[0];
      document.body.style.background = vorher[1];
    };
  }, []);

  useEffect(() => {
    let aktiv = true;
    const jetzt = new Date();
    const von = datumSchluessel(jetzt);
    const bis = datumSchluessel(plusMonate(jetzt, 12));
    const termineLaden = async () => {
      try {
        const antwort = await fetch(`${API_BASE_URL}/calendar/slots?from=${von}&to=${bis}&includePlanned=true`, {
          headers: { Accept: 'application/json' },
          credentials: 'omit',
        });
        if (!antwort.ok) throw new Error(`HTTP ${antwort.status}`);
        const daten = await antwort.json();
        // Review v1.8.64: unerwartete Antwort (kein Array) als Fehler anzeigen, nicht als leere Liste
        if (!Array.isArray(daten)) throw new Error('Antwort ist keine Terminliste');
        if (aktiv) setTermine(naechsteTermine(daten as TerminSlot[], new Date(), anzahl, tour));
      } catch (err) {
        console.warn('Nächste Termine: Termine konnten nicht geladen werden', err);
        if (aktiv) setFehler(true);
      }
    };
    const namenLaden = async () => {
      try {
        const antwort = await fetch(`${API_BASE_URL}/tour-types`, { headers: { Accept: 'application/json' }, credentials: 'omit' });
        if (!antwort.ok) return;
        const liste = await antwort.json();
        const zuordnung: Record<string, string> = {};
        for (const t of Array.isArray(liste) ? liste : []) {
          if (t && typeof t.code === 'string' && typeof t.name === 'string') zuordnung[t.code] = t.name;
        }
        if (aktiv) setNamen(zuordnung);
      } catch {
        // Namen sind optional, dann stehen die Codes der Tourarten
      }
    };
    termineLaden();
    namenLaden();
    return () => { aktiv = false; };
  }, [anzahl, tour]);

  // Höhe an das einbettende Fenster melden (nur eingebettet)
  useEffect(() => {
    if (typeof window === 'undefined' || window.parent === window) return;
    const melden = () => {
      const element = wurzel.current;
      if (!element) return;
      const hoehe = Math.ceil(element.getBoundingClientRect().height) + 2;
      window.parent.postMessage({ typ: HOEHE_NACHRICHT, hoehe }, '*');
    };
    melden();
    const beobachter = typeof ResizeObserver !== 'undefined' && wurzel.current ? new ResizeObserver(melden) : null;
    if (beobachter && wurzel.current) beobachter.observe(wurzel.current);
    window.addEventListener('resize', melden);
    return () => {
      if (beobachter) beobachter.disconnect();
      window.removeEventListener('resize', melden);
    };
  }, [termine, fehler, namen]);

  const kalender = (
    <a href="/booking" target="_top" rel="noopener">Zum Buchungskalender</a>
  );

  return (
    <div className="ff-termine" ref={wurzel}>
      {mitTitel && <h2 className="ff-termine-titel">Nächste Termine</h2>}
      {termine === null && !fehler && <p className="ff-termine-hinweis" role="status">Termine werden geladen ...</p>}
      {fehler && (
        <p className="ff-termine-hinweis" role="alert">Die Termine konnten gerade nicht geladen werden. {kalender}</p>
      )}
      {!fehler && termine !== null && termine.length === 0 && (
        <p className="ff-termine-hinweis">Zurzeit sind keine weiteren Termine eingestellt. {kalender}</p>
      )}
      {!fehler && termine !== null && termine.length > 0 && (
        <ul className="ff-termine-liste">
          {termine.map(t => (
            <li key={`${t.datum}-${t.zeit}`} className={`ff-termin${t.geplant ? ' ff-termin-geplant' : ''}`}>
              <div className="ff-termin-wann">
                <span className="ff-termin-datum">{datumText(t.datum)}</span>
                <span className="ff-termin-zeit">{t.zeit} Uhr</span>
              </div>
              <div className="ff-termin-was">
                {t.touren.length > 0 && <span className="ff-termin-tour">{tourText(t.touren, namen)}</span>}
                <span className="ff-termin-status">{t.geplant ? anfrageText(t.mindestPersonen) : platzText(t.frei)}</span>
              </div>
              <a
                className="ff-termin-knopf"
                href={terminLink(t.datum, t.zeit, tour)}
                target="_top"
                rel="noopener"
                aria-label={`${t.geplant ? 'Anfragen' : 'Buchen'}: ${datumText(t.datum)}, ${t.zeit} Uhr`}
              >
                {t.geplant ? 'Anfragen' : 'Buchen'}
              </a>
            </li>
          ))}
        </ul>
      )}
      <p className="ff-termine-fuss">
        <a href="/booking" target="_top" rel="noopener">Alle Termine im Kalender</a>
      </p>
    </div>
  );
};

export default NaechsteTermine;
